import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, differenceInMinutes } from 'date-fns';
import { ChevronRight } from 'lucide-react';
import { useData } from '@/contexts/DataContext';
import LogSheet, { type LogType } from '@/components/LogSheet';
import type { FeedEntry, SleepEntry, DiaperEntry } from '@/types';

function fmtTime(d: Date) { return format(d, 'h:mm a'); }
function fmtDur(mins: number) {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60), m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

type TimelineEvent =
  | { kind: 'feed';   entry: FeedEntry;   sortTime: Date }
  | { kind: 'sleep';  entry: SleepEntry;  sortTime: Date }
  | { kind: 'diaper'; entry: DiaperEntry; sortTime: Date };

const ADD_OPTIONS = [
  { type: 'feed'   as const, icon: '🍼', label: 'Feed' },
  { type: 'sleep'  as const, icon: '😴', label: 'Sleep' },
  { type: 'diaper' as const, icon: '🧷', label: 'Diaper' },
  { type: 'growth' as const, icon: '📊', label: 'Growth' },
];

// ── Add picker — bottom sheet ─────────────────────────────────────────────────
const AddPicker = ({ onClose, navigate }: { onClose: () => void; navigate: (p: string) => void }) => (
  <>
    <div className="fixed inset-0 z-[55] bg-black/40" onClick={onClose} />
    <div className="fixed bottom-0 left-0 right-0 z-[60] bg-white rounded-t-3xl pb-10 max-w-lg mx-auto" style={{ boxShadow: 'var(--shadow-modal)' }}>
      <div className="w-10 h-1 rounded-full bg-muted mx-auto mt-4 mb-5" />
      <p className="text-subtitle text-muted-foreground px-6 mb-3">What would you like to log?</p>
      <div className="grid grid-cols-4 gap-2 px-6">
        {ADD_OPTIONS.map(opt => (
          <button
            key={opt.type}
            onClick={() => { onClose(); navigate(`/add-${opt.type}`); }}
            className="flex flex-col items-center gap-1.5 py-4 rounded-2xl transition-colors active:scale-95"
            style={{ background: 'hsl(var(--muted))' }}
          >
            <span className="text-2xl">{opt.icon}</span>
            <span className="text-caption text-muted-foreground">{opt.label}</span>
          </button>
        ))}
      </div>
    </div>
  </>
);

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState = ({ onAdd }: { onAdd: () => void }) => (
  <div
    className="rounded-3xl px-5 py-8 flex flex-col items-center gap-3 text-center"
    style={{ background: 'hsl(var(--muted) / 0.4)' }}
  >
    <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
      <span style={{ fontSize: 28 }}>📋</span>
    </div>
    <div>
      <p style={{ fontSize: 15, fontWeight: 600, color: 'hsl(var(--foreground))' }}>
        Nothing logged yet today
      </p>
      <p className="text-caption text-muted-foreground mt-0.5">
        Tap below to log your first entry
      </p>
    </div>
    <button
      onClick={onAdd}
      className="mt-1 px-5 py-2.5 rounded-xl text-white text-sm font-semibold active:scale-95 transition-transform"
      style={{ background: 'hsl(var(--foreground))' }}
    >
      + Add entry
    </button>
  </div>
);

// ── Single event card ─────────────────────────────────────────────────────────
const EventCard = ({ ev, onEdit }: { ev: TimelineEvent; onEdit: () => void }) => {
  let iconEmoji: string;
  let label: string;
  let detail: string;
  let accentColor: string;
  let iconBg: string;

  if (ev.kind === 'feed') {
    const f = ev.entry;
    const isBottle = f.type === 'bottle';
    const isSolids = f.type === 'solids';
    iconEmoji = isBottle ? '🍼' : isSolids ? '🥄' : '🤱';
    label = isBottle ? 'Bottle feed' : isSolids ? 'Solids' : 'Breastfeed';
    detail = isBottle ? `${f.amount ?? 0} ml` : isSolids ? (f.foodName ?? 'Solids') : `${f.duration ?? 0} min`;
    accentColor = 'hsl(var(--feed-accent))';
    iconBg = 'hsl(var(--feed-bg))';
  } else if (ev.kind === 'sleep') {
    const s = ev.entry;
    const startHour = new Date(s.startTime).getHours();
    const dur = differenceInMinutes(new Date(s.endTime), new Date(s.startTime));
    iconEmoji = '😴';
    label = (startHour >= 6 && startHour < 20) ? 'Nap' : 'Night sleep';
    detail = fmtDur(Math.max(0, dur));
    accentColor = 'hsl(var(--sleep-accent))';
    iconBg = 'hsl(var(--sleep-bg))';
  } else {
    const d = ev.entry;
    iconEmoji = d.isPee && d.isPoop ? '💧' : d.isPoop ? '💩' : '💧';
    label = 'Diaper change';
    detail = d.isPee && d.isPoop ? 'Wet & soiled' : d.isPoop ? 'Soiled' : 'Wet';
    accentColor = 'hsl(var(--diaper-accent))';
    iconBg = 'hsl(var(--diaper-bg))';
  }

  return (
    <div
      className="bg-white rounded-xl p-3 flex items-center gap-2.5 w-full"
      style={{ boxShadow: 'var(--shadow-card)' }}
    >
      {/* Small colored icon circle */}
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: iconBg }}
      >
        <span style={{ fontSize: 14, lineHeight: 1 }}>{iconEmoji}</span>
      </div>

      {/* Text */}
      <div className="min-w-0 flex-1">
        <p style={{ fontSize: 13, fontWeight: 600, color: 'hsl(var(--foreground))' }}>{label}</p>
        <p style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>
          <span style={{ color: accentColor, fontWeight: 500 }}>{detail}</span>
        </p>
      </div>

      {/* Edit / navigate button */}
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors active:scale-95"
        style={{ background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))' }}
      >
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const DayTimeline = () => {
  const navigate = useNavigate();
  const { data } = useData();
  const [showPicker, setShowPicker] = useState(false);
  const [logSheet, setLogSheet] = useState<LogType | null>(null);

  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(todayStart); todayEnd.setDate(todayEnd.getDate() + 1);

  const events: TimelineEvent[] = [];

  data.feeds.forEach(f => {
    const ts = new Date(f.timestamp);
    if (ts >= todayStart && ts < todayEnd)
      events.push({ kind: 'feed', entry: f, sortTime: ts });
  });

  data.sleeps.forEach(s => {
    const start = new Date(s.startTime);
    if (start >= todayStart && start < todayEnd)
      events.push({ kind: 'sleep', entry: s, sortTime: start });
  });

  data.diapers.forEach(d => {
    const ts = new Date(d.timestamp);
    if (ts >= todayStart && ts < todayEnd)
      events.push({ kind: 'diaper', entry: d, sortTime: ts });
  });

  events.sort((a, b) => a.sortTime.getTime() - b.sortTime.getTime());

  const handleEdit = (ev: TimelineEvent) => {
    const typeMap: Record<string, LogType> = { feed: 'feed', sleep: 'sleep', diaper: 'diaper' };
    setLogSheet(typeMap[ev.kind] as LogType);
  };

  return (
    <div className="px-5 mt-6 lg:px-0">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-title text-foreground">Today's Timeline</h2>
        <button
          onClick={() => navigate('/history')}
          style={{ fontSize: 13, fontWeight: 500, color: 'hsl(var(--primary))' }}
        >
          See all
        </button>
      </div>

      {events.length === 0 ? (
        <EmptyState onAdd={() => setShowPicker(true)} />
      ) : (
        <div className="space-y-0">
          {events.map((ev, idx) => {
            const isLast = idx === events.length - 1;
            return (
              <div key={idx} className="flex gap-3">
                {/* Time label */}
                <div className="w-11 pt-2 flex-shrink-0 text-right">
                  <span className="text-caption text-muted-foreground">{fmtTime(ev.sortTime)}</span>
                </div>

                {/* Spine */}
                <div className="flex flex-col items-center flex-shrink-0 w-4">
                  <div
                    className="w-2 h-2 rounded-full mt-2.5 flex-shrink-0"
                    style={{
                      background:
                        ev.kind === 'feed'  ? 'hsl(var(--feed-accent))' :
                        ev.kind === 'sleep' ? 'hsl(var(--sleep-accent))' :
                                              'hsl(var(--diaper-accent))',
                    }}
                  />
                  {!isLast && (
                    <div
                      className="w-px flex-1 mt-1 min-h-[16px]"
                      style={{ background: 'hsl(var(--border) / 0.4)' }}
                    />
                  )}
                </div>

                {/* Card */}
                <div className="flex-1 pb-3">
                  <EventCard ev={ev} onEdit={() => handleEdit(ev)} />
                </div>
              </div>
            );
          })}

          {/* Add entry */}
          <button
            onClick={() => setShowPicker(true)}
            className="w-full mt-1 py-2.5 rounded-xl text-sm text-muted-foreground transition-colors"
            style={{
              border: '1px dashed hsl(var(--border))',
              fontSize: 14,
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'hsl(var(--muted) / 0.5)';
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            + Add
          </button>
        </div>
      )}

      {/* Add picker — bottom sheet */}
      {showPicker && <AddPicker onClose={() => setShowPicker(false)} navigate={navigate} />}

      {/* Log sheet */}
      {logSheet && <LogSheet type={logSheet} isOpen={!!logSheet} onClose={() => setLogSheet(null)} />}
    </div>
  );
};

export default DayTimeline;
